import { Modal } from './Modal';

/**
 * Explains, in plain language, exactly how Navigator computes its scores.
 * Every claim here mirrors packages/scoring (fit score) and the proposal
 * readiness formula — keep this in sync if those change.
 */
export function MethodologyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onClose={onClose} title="Methodology & scoring" wide>
      <div className="stack" style={{ gap: 'var(--s5)' }}>
        <section>
          <h3 style={{ margin: '0 0 var(--s2)', fontSize: '1rem' }}>Fit score (0–100)</h3>
          <p className="text-muted" style={{ marginTop: 0 }}>
            Each opportunity is scored against your organization profile across nine
            weighted dimensions. The score is a weighted average of the dimensions we
            have data for:
          </p>
          <ul className="nudge-missing" style={{ gap: 'var(--s1)' }}>
            <li>NAICS / program match — 18%</li>
            <li>Capability match (keyword overlap with your profile) — 18%</li>
            <li>Eligibility / set-aside alignment — 14%</li>
            <li>Past performance — 10%</li>
            <li>Size fit — 10%</li>
            <li>Geography — 10%</li>
            <li>Competition — 5%</li>
            <li>Proposal complexity — 5%</li>
            <li>Time remaining — 5%</li>
          </ul>
          <p className="text-muted">
            Dimensions with missing data are <strong>left out of the average</strong>,
            not scored as zero — but they lower the confidence badge (High: 8+ dimensions
            known, Medium: 5–7, Low: fewer). The “Score improves if you…” tips tell you
            exactly which profile fields to fill in.
          </p>
        </section>
        <section>
          <h3 style={{ margin: '0 0 var(--s2)', fontSize: '1rem' }}>Analyze verdict</h3>
          <p className="text-muted" style={{ marginTop: 0 }}>
            When you upload a solicitation, Navigator reads the document and scores it
            against your profile:
          </p>
          <ul className="nudge-missing" style={{ gap: 'var(--s1)' }}>
            <li><strong>GO (75+)</strong> — worth pursuing; fit is strong and no blockers found.</li>
            <li><strong>CAUTION (50–74)</strong> — possible fit; review the flags before investing effort.</li>
            <li><strong>NO-GO (below 50)</strong> — blockers or weak fit; bidding here is likely wasted effort.</li>
          </ul>
        </section>
        <section>
          <h3 style={{ margin: '0 0 var(--s2)', fontSize: '1rem' }}>Proposal readiness</h3>
          <p className="text-muted" style={{ marginTop: 0 }}>
            The readiness ring in each proposal workspace is 40% narrative sections
            complete, 30% compliance checklist, and 10% each for assigning an owner,
            attaching a file, and setting a deadline.
          </p>
        </section>
        <section>
          <h3 style={{ margin: '0 0 var(--s2)', fontSize: '1rem' }}>GovCon readiness</h3>
          <p className="text-muted" style={{ marginTop: 0 }}>
            The GovCon checklist score measures <strong>checklist completeness only</strong> —
            it is not a certification and does not guarantee contract eligibility.
          </p>
        </section>
        <p className="text-sm text-faint" style={{ margin: 0 }}>
          Scores are decision support, not guarantees. They only know what your
          profile and the opportunity record tell them — the fuller both are, the
          sharper the scores.
        </p>
      </div>
    </Modal>
  );
}
